import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { centsToInput, formatCents, parseEuro } from '@/components/repairs';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Switch } from '@/components/ui/primitives';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import type { Paginated } from '@/lib/types';

interface Rate {
  id: string;
  name: string;
  priceCents: number;
  active: boolean;
  sortOrder: number;
  userCount: number;
}
interface StaffUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  isCustomer?: boolean;
}

const onError = (e: unknown) => toast.error(errorMessage(e));

/** Zeile der Zuordnungstabelle: Sätze eines Mitarbeiters und Vorauswahl. */
function UserRow({ user, rates }: { user: StaffUser; rates: Rate[] }) {
  const qc = useQueryClient();
  const key = ['hourly-rates', 'user', user.id];
  const { data } = useQuery({ queryKey: key, queryFn: () => api<{ rateIds: string[]; defaultRateId: string | null }>(`/hourly-rates/users/${user.id}`) });
  const save = useMutation({
    mutationFn: (v: { rateIds: string[]; defaultRateId: string | null }) => api<{ rateIds: string[]; defaultRateId: string | null }>(`/hourly-rates/users/${user.id}`, { method: 'PUT', body: v }),
    onSuccess: (v) => {
      qc.setQueryData(key, v);
      qc.invalidateQueries({ queryKey: ['hourly-rates'], exact: true });
    },
    onError,
  });
  if (!data) return null;
  const toggle = (id: string) => {
    const rateIds = data.rateIds.includes(id) ? data.rateIds.filter((x) => x !== id) : [...data.rateIds, id];
    const defaultRateId = data.defaultRateId && rateIds.includes(data.defaultRateId) ? data.defaultRateId : (rateIds[0] ?? null);
    save.mutate({ rateIds, defaultRateId });
  };
  return (
    <TR>
      <TD className="whitespace-nowrap">
        <p className="font-medium">
          {user.firstName} {user.lastName}
        </p>
        <p className="text-xs text-muted-foreground">{user.email}</p>
      </TD>
      {rates.map((r) => (
        <TD key={r.id} className="text-center">
          <div className="flex flex-col items-center gap-1">
            <Checkbox checked={data.rateIds.includes(r.id)} onCheckedChange={() => toggle(r.id)} aria-label={`${r.name} für ${user.firstName} ${user.lastName}`} />
            {data.rateIds.includes(r.id) && (
              <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <input type="radio" name={`default-${user.id}`} checked={data.defaultRateId === r.id} onChange={() => save.mutate({ rateIds: data.rateIds, defaultRateId: r.id })} />
                Vorauswahl
              </label>
            )}
          </div>
        </TD>
      ))}
    </TR>
  );
}

export function RatesPage() {
  const qc = useQueryClient();
  const [confirmDialog, confirm] = useConfirm();
  const [edit, setEdit] = useState<{ id: string | null; name: string; price: string; active: boolean } | null>(null);
  const { data } = useQuery({ queryKey: ['hourly-rates'], queryFn: () => api<{ items: Rate[] }>('/hourly-rates') });
  const { data: users } = useQuery({ queryKey: ['users', 'staff-rates'], queryFn: () => api<Paginated<StaffUser>>('/users?pageSize=100&status=ACTIVE&kind=staff') });
  const saved = (v: { items: Rate[] }) => {
    qc.setQueryData(['hourly-rates'], v);
    setEdit(null);
  };
  const save = useMutation({
    mutationFn: () => {
      const body = { name: edit!.name, priceCents: parseEuro(edit!.price) ?? 0, active: edit!.active };
      return edit!.id ? api<{ items: Rate[] }>(`/hourly-rates/${edit!.id}`, { method: 'PUT', body }) : api<{ items: Rate[] }>('/hourly-rates', { method: 'POST', body });
    },
    onSuccess: saved,
    onError,
  });
  const del = useMutation({ mutationFn: (id: string) => api<{ items: Rate[] }>(`/hourly-rates/${id}`, { method: 'DELETE' }), onSuccess: saved, onError });
  const rates = data?.items ?? [];
  const activeRates = rates.filter((r) => r.active);
  const staff = (users?.items ?? []).filter((u) => !u.isCustomer);

  return (
    <div className="grid gap-4">
      {confirmDialog}
      <PageHeader title="Stundensätze" description="Sätze für Arbeitszeit in Tickets und Reparaturen. Techniker wählen beim Buchen aus den ihnen zugeordneten Sätzen; die Abrechnung rechnet damit." />
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>Sätze</CardTitle>
            <CardDescription>Beträge netto je Stunde. Änderungen gelten für neue Buchungen.</CardDescription>
          </div>
          <Button size="sm" onClick={() => setEdit({ id: null, name: '', price: '', active: true })}>
            <Plus /> Stundensatz
          </Button>
        </CardHeader>
        <CardContent className="grid gap-2">
          {edit && (
            <form
              className="grid items-end gap-2 rounded-md border p-3 sm:grid-cols-[1fr_10rem_auto_auto]"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
            >
              <Field label="Bezeichnung" htmlFor="rate-name">
                <Input id="rate-name" required value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="z. B. Techniker" />
              </Field>
              <Field label="€ je Stunde (netto)" htmlFor="rate-price" error={edit.price && parseEuro(edit.price) === null ? 'Ungültig' : undefined}>
                <Input id="rate-price" required inputMode="decimal" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} placeholder="85,00" />
              </Field>
              <label className="flex h-9 items-center gap-2 text-sm">
                <Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} /> Aktiv
              </label>
              <div className="flex gap-2">
                <Button type="submit" loading={save.isPending} disabled={!edit.name.trim() || parseEuro(edit.price) === null}>
                  Speichern
                </Button>
                <Button type="button" variant="ghost" onClick={() => setEdit(null)}>
                  Abbrechen
                </Button>
              </div>
            </form>
          )}
          {rates.length === 0 && !edit && <p className="text-sm text-muted-foreground">Noch keine Stundensätze angelegt.</p>}
          {rates.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
              <span>
                <span className="font-medium">{r.name}</span> · {formatCents(r.priceCents)}/h {!r.active && <Badge variant="outline">inaktiv</Badge>}
                <span className="ml-2 text-xs text-muted-foreground">{r.userCount} Mitarbeiter</span>
              </span>
              <span className="flex gap-1">
                <Button variant="ghost" size="icon" className="size-8" aria-label={`${r.name} bearbeiten`} onClick={() => setEdit({ id: r.id, name: r.name, price: centsToInput(r.priceCents), active: r.active })}>
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive hover:text-destructive"
                  aria-label={`${r.name} löschen`}
                  onClick={async () => (await confirm({ title: `Stundensatz „${r.name}“ löschen?`, description: 'Bereits gebuchte Zeiten behalten ihren Satz.', confirmLabel: 'Löschen', destructive: true })) && del.mutate(r.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </span>
            </div>
          ))}
        </CardContent>
      </Card>

      {activeRates.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Zuordnung</CardTitle>
            <CardDescription>Welche Sätze ein Mitarbeiter wählen darf; die Vorauswahl wird beim Buchen vorgeschlagen. Ohne Zuordnung wird ohne Satz gebucht (Preis in Powerbird).</CardDescription>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>Mitarbeiter</TH>
                  {activeRates.map((r) => (
                    <TH key={r.id} className="text-center">
                      {r.name}
                      <span className="block font-normal normal-case">{formatCents(r.priceCents)}/h</span>
                    </TH>
                  ))}
                </TR>
              </THead>
              <TBody>
                {staff.length === 0 && <EmptyRow colSpan={activeRates.length + 1}>Keine Mitarbeiter.</EmptyRow>}
                {staff.map((u) => (
                  <UserRow key={u.id} user={u} rates={activeRates} />
                ))}
              </TBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
